export type Room = [number, number, number]; // [adults, children, adolescents]

export const findAllHotelRoomCombinations = (
  availableRooms: Room[],
  adults: number,
  children: number,
  adolescents: number,
): Room[][] => {
  if (adults === 0 && children === 0 && adolescents === 0) {
    return [];
  }

  if (availableRooms.length === 0) {
    return [];
  }

  const dp: Record<string, Set<string>> = {};

  const getStateKey = (a: number, c: number, ad: number) => `${a},${c},${ad}`;

  const getCombinations = (
    remainingAdults: number,
    remainingChildren: number,
    remainingAdolescents: number,
  ): Set<string> => {
    if (
      remainingAdults === 0 &&
      remainingChildren === 0 &&
      remainingAdolescents === 0
    ) {
      return new Set(['']); // Base case
    }

    const key = getStateKey(
      remainingAdults,
      remainingChildren,
      remainingAdolescents,
    );
    if (dp[key]) return dp[key];

    const result = new Set<string>();

    for (const room of availableRooms) {
      const [roomAdults, roomChildren, roomAdolescents] = room;

      const baseCases = [
        {
          valid:
            remainingAdults >= roomAdults &&
            remainingChildren >= roomChildren &&
            remainingAdolescents >= roomAdolescents,
          newAdults: remainingAdults - roomAdults,
          newChildren: remainingChildren - roomChildren,
          newAdolescents: remainingAdolescents - roomAdolescents,
        },
        {
          valid:
            remainingChildren >=
              roomAdults - Math.min(remainingAdults, roomAdults) &&
            remainingChildren -
              (roomAdults - Math.min(remainingAdults, roomAdults)) >=
              roomChildren &&
            remainingAdolescents >= roomAdolescents,
          newAdults:
            remainingAdults -
            roomAdults +
            (roomAdults - Math.min(remainingAdults, roomAdults)),
          newChildren:
            remainingChildren -
            (roomAdults - Math.min(remainingAdults, roomAdults)) -
            roomChildren,
          newAdolescents: remainingAdolescents - roomAdolescents,
        },
        {
          valid:
            remainingAdolescents >=
              roomAdults - Math.min(remainingAdults, roomAdults) &&
            remainingChildren >= roomChildren &&
            remainingAdolescents -
              (roomAdults - Math.min(remainingAdults, roomAdults)) >=
              roomAdolescents,
          newAdults:
            remainingAdults -
            roomAdults +
            (roomAdults - Math.min(remainingAdults, roomAdults)),
          newChildren: remainingChildren - roomChildren,
          newAdolescents:
            remainingAdolescents -
            (roomAdults - Math.min(remainingAdults, roomAdults)) -
            roomAdolescents,
        },
        {
          valid:
            remainingAdults < roomAdults &&
            remainingChildren > roomChildren &&
            remainingAdolescents > roomAdolescents &&
            remainingAdults +
              remainingChildren +
              remainingAdolescents -
              roomChildren -
              roomAdolescents >=
              roomAdults,
          newAdults:
            remainingAdults +
            remainingChildren +
            remainingAdolescents -
            roomAdults -
            roomChildren -
            roomAdolescents,
          newChildren: 0,
          newAdolescents: 0,
        },
      ];

      for (const {
        valid,
        newAdults,
        newChildren,
        newAdolescents,
      } of baseCases) {
        if (valid) {
          const rest = getCombinations(newAdults, newChildren, newAdolescents);
          for (const comb of rest) {
            const parsed = comb
              ? [[...room], ...(JSON.parse(comb) as Room[])]
              : [[...room]];

            result.add(
              JSON.stringify(
                parsed.toSorted(
                  (a, b) => a[0] - b[0] || a[1] - b[1] || a[2] - b[2],
                ),
              ),
            );
          }
        }
      }
    }

    dp[key] = result;
    return result;
  };

  return Array.from(getCombinations(adults, children, adolescents))
    .filter((str) => str.length > 0)
    .map((str) => JSON.parse(str) as Room[]);
};
